// THE PINNED TOP OF EVERY SIGNED-IN PAGE (owner, 2026-10-13: "make sure the
// most top bar, that has BidLight name and the contractor logo and name ...
// sticks always on top of any sticking bar and stays visible in all pages").
//
// One sticky block holds the site header AND the Coordination tab strip, so
// the two can never slide over or under each other: they move as one. The
// block's live height is published on :root, in LAYOUT px (offsetHeight - the
// same unit a CSS `top:` is written in under `html { zoom }`):
//
//   --app-bars-h    header + tab strip (the strip is absent on most pages)
//   --app-header-h  the header alone
//
// A page bar that sticks to the WINDOW uses `top: var(--app-bars-h, 0px)` and
// so pins just below this block. A bar that sticks inside its own scroll box
// (a table that scrolls, a side panel) keeps `top: 0` - that box is already
// below the header, and an offset there would only open a gap.
//
// Layering: 990 is above every page bar (those stay well under 100) and
// below everything that must cover the header - antd drawers and modals
// (1000+), dropdowns/tooltips (1030-1070), Bootstrap/MDB modals (1040/1050),
// and the deliberate full-screen views (Gantt 1000, issue resolve 1500).
// Changing it? Keep `.share-button-group` (share.scss) one above it.
//
// On unmount the variables are REMOVED, not zeroed, so every `var(..., x)`
// falls back to what it did before this existed (signed-out pages, tests).
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

export const BARS_VAR = "--app-bars-h";
export const HEADER_VAR = "--app-header-h";

export function publishBarHeights(el, root = document.documentElement) {
  if (!el || !root) return;
  const header = el.querySelector(".header");
  root.style.setProperty(BARS_VAR, `${el.offsetHeight || 0}px`);
  root.style.setProperty(HEADER_VAR, `${(header ? header.offsetHeight : el.offsetHeight) || 0}px`);
}

// Where the pinned block ends, in VIEWPORT (rendered) px - the space an
// IntersectionObserver rootMargin or a client rect lives in. 0 when absent.
export function appBarsBottom() {
  if (typeof document === "undefined") return 0;
  const el = document.querySelector(".app-topbars");
  if (!el) return 0;
  const b = el.getBoundingClientRect().bottom;
  return Number.isFinite(b) && b > 0 ? b : 0;
}

export default function AppTopBars({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return undefined;
    const publish = () => publishBarHeights(el, root);
    publish();
    // The tab strip mounting/unmounting on navigation, the company chip
    // loading, the header wrapping on a narrow window: all change this
    // block's height, and the observer sees each one.
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    window.addEventListener("resize", publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty(BARS_VAR);
      root.style.removeProperty(HEADER_VAR);
    };
  }, []);
  return (
    <div ref={ref} className="app-topbars" data-testid="app-topbars">
      {children}
    </div>
  );
}

AppTopBars.propTypes = {
  children: PropTypes.node,
};
